<template>
  <div class="w-full">
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div class="flex flex-wrap items-center gap-3">
        <h3 class="m-0 text-3xl font-bold text-blue-900">{{ $t('materials_listing_title') }}</h3>
      </div>
      <b-tabs type="is-toggle" size="is-small" v-model="activeSubTab" class="mb-0 stock-warehouse-view-toggle">
        <b-tab-item :label="$t('expanded_listing')"></b-tab-item>
        <b-tab-item :label="$t('listing_by_unique_material_id')"></b-tab-item>
      </b-tabs>
      <div class="flex flex-wrap items-center gap-2">
        <b-button
          v-if="activeSubTab === 1"
          type="is-primary"
          size="is-small"
          icon-left="plus"
          icon-pack="fas"
          @click="openAddMaterialModal"
        >
          {{ $t('create_unique_material') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="file-export"
          icon-pack="fas"
          :loading="isExporting"
          :disabled="isExporting"
          @click="onExportInventorySheet"
        >
          {{ $t('export') }}
        </b-button>
      </div>
    </div>
    <b-collapse
      class="card filters-collapse mt-2"
      v-model="showFilters"
    >
      <template #trigger>
        <div class="card-header filters-trigger">
          <p class="card-header-title">
            {{ $t('filters') }}
          </p>
          <a class="card-header-icon">
            <b-icon pack="fa" v-show="!showFilters" icon="plus-circle" />
            <b-icon pack="fa" v-show="showFilters" icon="minus-circle" />
          </a>
        </div>
      </template>

      <div class="panel" style="border-width: 0">
        <div class="grid grid-cols-1 md:grid-cols-6 gap-3">


          <b-field :label="$t('material')">
            <warehouse-catalog-autocomplete
              v-model="inventoryFilters.warehouse_material_id"
              :project-id="projectId"
              endpoint="materials"
              response-key="materials"
              order-field="code"
              :placeholder="$t('material')"
              :formatter="formatMaterialOption"
              @input="onFiltersChange"
            />
          </b-field>

          <b-field :label="$t('provider')">
            <warehouse-catalog-autocomplete
              v-model="inventoryFilters.warehouse_provider_id"
              :project-id="projectId"
              endpoint="providers"
              response-key="providers"
              order-field="name"
              :placeholder="$t('provider')"
              :formatter="formatProviderOption"
              @input="onFiltersChange"
            />
          </b-field>
          <!-- filtro por numero de pedido -->
          <b-field :label="$t('order_number')">
            <b-input
              v-model.trim="inventoryFilters.material_order_number"
              :placeholder="$t('order_number')"
              @input="onFiltersChangeDebounced"
            />
          </b-field>

          <b-field :label="$t('activity')">
            <b-select
              :value="parentActivityId"
              expanded
              :loading="isActivitiesLoading"
              @input="onParentActivityChange"
            >
              <option :value="null">{{ $t('all') }}</option>
              <option
                v-for="a in parentActivities"
                :key="`act-${a.id}`"
                :value="a.id"
              >
                {{ (a.planning_code ? `${a.planning_code} - ` : '') + (a.name || `#${a.id}`) }}
              </option>
            </b-select>
          </b-field>

          <b-field :label="$t('subactivity')">
            <b-select
              v-model="subactivityId"
              expanded
              :disabled="!parentActivityId || subactivities.length === 0"
              :loading="isActivitiesLoading"
              @input="onFiltersChange"
            >
              <option :value="null">{{ $t('all') }}</option>
              <option
                v-for="s in subactivities"
                :key="`sub-${s.id}`"
                :value="s.id"
              >
                {{ (s.planning_code ? `${s.planning_code} - ` : '') + (s.name || `#${s.id}`) }}
              </option>
            </b-select>
          </b-field>

          <b-field :label="$t('search')">
            <b-input
              v-model.trim="inventoryFilters.search"
              :placeholder="$t('search')"
              @input="onFiltersChangeDebounced"
            />
          </b-field>
        </div>
      </div>
    </b-collapse>

    <div v-if="activeSubTab === 0">
      <materials-list-table-bulks
        :project-id="projectId"
        :with-bulk="false"
        :filters="tableFilters"
        @deleted="onInventoryDeleted"
      />
    </div>

    <div v-else class="w-full border border-gray-300 relative overflow-x-auto">
      <b-loading
        :active="isGroupedCatalogLoading"
        :is-full-page="false"
      />
      <b-table
        class="warehouse-grouped-table"
        :data="groupedRows"
        detailed
        detail-key="group_key"
        :has-detailed-visible="row => row.elements_associated > 0"
        :row-class="groupedTableRowClass"
        icon-pack="fas"
      >
        <template>
          <!-- <b-table-column field="display_id" :label="$t('id')" width="90" v-slot="props">
            {{ props.row.display_id }}
          </b-table-column> -->

          <b-table-column field="unique_material_id" :label="$t('unique_material_id')" v-slot="props">
            <template v-if="props.row.is_pending_assignment">
              <span class="warehouse-pending-unique-label">{{ $t('warehouse_inventory_pending_unique_material_label') }}</span>
            </template>
            <template v-else>
              {{ props.row.unique_material_id }}
            </template>
          </b-table-column>

          <b-table-column field="description" :label="$t('description')" v-slot="props">
            <template v-if="props.row.is_pending_assignment">
              <span class="warehouse-pending-description">{{ $t('warehouse_inventory_pending_group_description') }}</span>
            </template>
            <template v-else>
              {{ props.row.description }}
            </template>
          </b-table-column>

          <b-table-column field="category" :label="$t('category')" width="150" cell-class="warehouse-stock-text-cell" v-slot="props">
            <span class="warehouse-stock-text-cell__content">{{ props.row.category }}</span>
          </b-table-column>

          <b-table-column field="order_number" :label="$t('order_number')" width="150" cell-class="warehouse-stock-text-cell" v-slot="props">
            <span class="warehouse-stock-text-cell__content">{{ props.row.order_number }}</span>
          </b-table-column>

          <b-table-column field="provider" :label="$t('provider')" width="150" cell-class="warehouse-stock-text-cell" v-slot="props">
            <span class="warehouse-stock-text-cell__content">{{ props.row.provider }}</span>
          </b-table-column>

          <b-table-column field="stock" :label="$t('stock')" centered v-slot="props">
            {{ formatQuantity(props.row.stock) }}
          </b-table-column>

          <b-table-column field="unit" :label="$t('unit')" v-slot="props">
            {{ props.row.unit }}
          </b-table-column>

          <!-- <b-table-column field="drawing_number" :label="$t('drawing_number')" v-slot="props">
            {{ props.row.drawing_number }}
          </b-table-column>

          <b-table-column field="tag_kks" :label="$t('tag_kks')" v-slot="props">
            {{ props.row.tag_kks }}
          </b-table-column> -->

          <b-table-column field="activity" :label="$t('activity')" v-slot="props">
            {{ props.row.activity }}
          </b-table-column>

          <b-table-column field="elements_associated" :label="$t('associated_elements')" centered v-slot="props">
            {{ props.row.elements_associated }}
          </b-table-column>

          <b-table-column field="actions" :label="$t('actions')" width="110" centered v-slot="props">
            <div
              v-if="!props.row.is_pending_assignment"
              class="flex items-center justify-center"
            >
              <b-tooltip :label="$t('edit')" position="is-top">
                <button
                  type="button"
                  class="button is-small is-light"
                  @click.stop="openEditGroupedMaterial(props.row)"
                >
                  <span class="icon is-small">
                    <i class="fas fa-pencil-alt"></i>
                  </span>
                </button>
              </b-tooltip>
              <b-tooltip
                v-if="Number(props.row.elements_associated) === 0"
                :label="$t('delete')"
                position="is-top"
              >
                <button
                  type="button"
                  class="button is-small is-light ml-1"
                  :disabled="deletingMaterialId === props.row.warehouse_material_id"
                  @click.stop="onDeleteGroupedMaterial(props.row)"
                >
                  <span class="icon is-small has-text-danger">
                    <i class="fas fa-trash"></i>
                  </span>
                </button>
              </b-tooltip>
            </div>
          </b-table-column>
        </template>

        <template #detail="props">
          <div class="p-2 bg-gray-100 relative min-h-[48px]">
            <div class="warehouse-detail-scroll">
              <materials-list-table-bulks
                :project-id="projectId"
                :with-bulk="false"
                :warehouse-material-id="props.row.warehouse_material_id"
                :without-warehouse-material="Boolean(props.row.is_pending_assignment)"
                :per-page="10"
                @deleted="onInventoryDeleted"
              />
            </div>
          </div>
        </template>
      </b-table>
    </div>

    <WarehouseAddMaterialModal
      ref="addMaterialModal"
      :project-id="projectId"
      :allow-edit="true"
      @saved="onWarehouseMaterialSaved"
    />
  </div>
</template>

<script>
import MaterialsListTableBulks from './materials-list-table-bulks.vue'
import WarehouseAddMaterialModal from '../../admin/warehouse/warehouse-add-material-modal.vue'
import WarehouseCatalogAutocomplete from './warehouse-catalog-autocomplete.vue'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'stock-warehouse',
  components: {
    MaterialsListTableBulks,
    WarehouseAddMaterialModal,
    WarehouseCatalogAutocomplete,
  },
  props: {
    projectId: { type: Number, required: true },
  },
  data: () => ({
    activeSubTab: 0,
    isExporting: false,
    showFilters: false,
    isCatalogsLoading: false,
    materialOptions: [],
    providerOptions: [],
    unitOptions: [],
    parentActivityId: null,
    subactivityId: null,
    parentActivities: [],
    subactivities: [],
    isActivitiesLoading: false,
    inventoryFilters: {
      warehouse_material_id: null,
      warehouse_provider_id: null,
      unit_id: null,
      material_order_number: '',
      search: '',
    },
    inventoryFiltersDebounce: null,
    isGroupedCatalogLoading: false,
    deletingMaterialId: null,
    /** Total acumulado tras cargar todas las páginas; lo dejo por si en el futuro queremos
     *  mostrar "X materiales" en la cabecera. La vista no pagina. */
    groupedTableTotal: 0,
    groupedCatalogRaw: [],
  }),
  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
    groupedRows() {
      return this.mapMaterialsCatalogToGroupedRows(this.groupedCatalogRaw)
    },
    tableFilters() {
      const selectedActivityId = this.subactivityId != null && this.subactivityId !== ''
        ? Number(this.subactivityId)
        : (this.parentActivityId != null && this.parentActivityId !== '' ? Number(this.parentActivityId) : null)
      return {
        material_activity_id: Number.isFinite(selectedActivityId) && selectedActivityId > 0 ? selectedActivityId : null,
        warehouse_material_id: this.inventoryFilters.warehouse_material_id,
        warehouse_provider_id: this.inventoryFilters.warehouse_provider_id,
        unit_id: this.inventoryFilters.unit_id,
        material_order_number: this.inventoryFilters.material_order_number,
        search: this.inventoryFilters.search,
      }
    },
  },
  watch: {
    projectId() {
      this.resetGroupedWarehouseState()
      this.resetFlatFilters()
      this.loadParentActivities()
      if (this.activeSubTab === 1) {
        this.loadMaterialsGroupedCatalog()
      } else {
        this.loadCatalogs()
      }
    },
    activeSubTab(val) {
      if (val === 1 && this.projectId) {
        this.loadMaterialsGroupedCatalog()
      }
      if (val === 0 && this.projectId) {
        this.loadCatalogs()
        if (!this.parentActivities || this.parentActivities.length === 0) {
          this.loadParentActivities()
        }
      }
    },
  },
  created() {
    if (this.projectId) {
      this.loadCatalogs()
      this.loadParentActivities()
    }
  },
  methods: {
    resetFlatFilters() {
      this.inventoryFilters = {
        warehouse_material_id: null,
        warehouse_provider_id: null,
        unit_id: null,
        material_order_number: '',
        search: '',
      }
      this.parentActivityId = null
      this.subactivityId = null
      this.parentActivities = []
      this.subactivities = []
    },
    onFiltersChange() {
      if (this.activeSubTab === 1 && this.projectId) {
        this.loadMaterialsGroupedCatalog()
      }
    },
    onFiltersChangeDebounced() {
      if (this.inventoryFiltersDebounce) clearTimeout(this.inventoryFiltersDebounce)
      this.inventoryFiltersDebounce = setTimeout(() => {
        this.onFiltersChange()
      }, 350)
    },
    async loadCatalogs() {
      if (!this.projectId) {
        this.materialOptions = []
        this.providerOptions = []
        this.unitOptions = []
        return
      }
      this.isCatalogsLoading = true
      try {
        const companyId = this.company && this.company.id ? Number(this.company.id) : null
        const [materialsRes, providersRes, unitsRes] = await Promise.all([
          axios.get(`/api/v2/project/${this.projectId}/warehouse/materials`, {
            params: { per_page: 100, page: 1, order_field: 'code', order_dir: 'asc' },
          }),
          axios.get(`/api/v2/project/${this.projectId}/warehouse/providers`, {
            params: { per_page: 100, page: 1, order_field: 'name', order_dir: 'asc' },
          }),
          companyId
            ? axios.get(`/api/v2/company/${companyId}/measurement-units`, { params: { per_page: 300 } })
            : Promise.resolve({ data: {} }),
        ])
        this.materialOptions = this.extractArrayFromResponse(materialsRes.data, 'materials')
        this.providerOptions = this.extractArrayFromResponse(providersRes.data, 'providers')
        this.unitOptions = this.extractArrayFromResponse(unitsRes.data, 'units')
      } catch (e) {
        this.materialOptions = []
        this.providerOptions = []
        this.unitOptions = []
      } finally {
        this.isCatalogsLoading = false
      }
    },
    async loadParentActivities() {
      if (!this.projectId) return
      this.isActivitiesLoading = true
      try {
        const { data } = await axios.get('/api/v2/activities', {
          params: {
            p: this.projectId,
            per_page: 2000,
            page: 1,
            order_field: 'planning_code',
            order_dir: 'asc',
            light: 1,
          },
        })
        const list = (data && Array.isArray(data.activities)) ? data.activities : []
        this.parentActivities = list
      } catch (e) {
        this.parentActivities = []
      } finally {
        this.isActivitiesLoading = false
      }
    },
    async loadSubactivities(activityId) {
      const parentId = activityId != null && activityId !== '' ? Number(activityId) : null
      if (!this.projectId || !Number.isFinite(parentId) || parentId < 1) {
        this.subactivities = []
        return
      }
      this.isActivitiesLoading = true
      try {
        const { data } = await axios.get(`/api/v2/activity/${parentId}/subactivities`, {
          params: { order_by: 'planning_code', order_dir: 'asc' },
        })
        const list = (data && Array.isArray(data.subactivities)) ? data.subactivities : []
        this.subactivities = list
      } catch (e) {
        this.subactivities = []
      } finally {
        this.isActivitiesLoading = false
      }
    },
    onParentActivityChange(val) {
      const n = val != null && val !== '' ? Number(val) : null
      this.parentActivityId = Number.isFinite(n) && n > 0 ? n : null
      this.subactivityId = null
      this.subactivities = []
      if (this.parentActivityId) {
        this.loadSubactivities(this.parentActivityId)
      }
      this.onFiltersChange()
    },
    resetGroupedWarehouseState() {
      this.groupedTableTotal = 0
      this.groupedCatalogRaw = []
    },
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    mapMaterialsCatalogToGroupedRows(list) {
      if (!Array.isArray(list) || list.length === 0) return []
      return list.map((m) => {
        const pending = Boolean(m.is_inventory_unassigned_summary)
        const unitLabel = m.unit_label
          || (m.unit && (m.unit.code || m.unit.name))
          || '-'
        const providerLabel = m.provider_name
          || (m.provider && m.provider.name)
          || '-'
        return {
          group_key: pending ? '__without_unique_id__' : `mat_${m.id}`,
          warehouse_material_id: m.id,
          display_id: pending ? '—' : (m.id != null ? String(m.id) : '-'),
          unique_material_id: pending ? '-' : (m.code != null && m.code !== '' ? String(m.code) : '-'),
          description: pending ? '' : (m.description || '-'),
          category: pending ? '-' : (m.category || '-'),
          order_number: pending ? '-' : (m.order_number || '-'),
          provider: pending ? '-' : providerLabel,
          unit: pending ? '-' : unitLabel,
          drawing_number: pending ? '-' : (m.drawing_number || '-'),
          tag_kks: pending ? '-' : (m.tag_kks || '-'),
          activity: pending ? '-' : this.formatMaterialActivityLabel(m.activity),
          stock: Number(m.stock || 0),
          elements_associated: Number(m.elements_associated || 0),
          is_pending_assignment: pending,
        }
      })
    },
    formatMaterialActivityLabel(activity) {
      if (!activity) return '-'
      const code = activity.planning_code ? `${activity.planning_code} - ` : ''
      return code + (activity.name || `#${activity.id}`)
    },
    async loadMaterialsGroupedCatalog() {
      if (!this.projectId) {
        this.groupedCatalogRaw = []
        return
      }

      this.isGroupedCatalogLoading = true
      try {
        const f = this.tableFilters || {}
        const baseParams = {
          per_page: 100,
          order_field: 'code',
          order_dir: 'asc',
          with_inventory_totals: 1,
        }
        if (f.warehouse_material_id != null && f.warehouse_material_id !== '') {
          baseParams.warehouse_material_id = Number(f.warehouse_material_id)
        }
        if (f.warehouse_provider_id != null && f.warehouse_provider_id !== '') {
          baseParams.warehouse_provider_id = Number(f.warehouse_provider_id)
        }
        if (f.unit_id != null && f.unit_id !== '') {
          baseParams.unit_id = Number(f.unit_id)
        }
        if (f.material_activity_id != null && f.material_activity_id !== '') {
          baseParams.material_activity_id = Number(f.material_activity_id)
        }
        const orderNumber = f.material_order_number != null ? String(f.material_order_number).trim() : ''
        if (orderNumber !== '') {
          baseParams.material_order_number = orderNumber
        }
        const search = f.search != null ? String(f.search).trim() : ''
        if (search !== '') {
          baseParams.search = search
        }

        const allMaterials = []
        let currentPage = 1
        let lastPage = 1

        const MAX_PAGES = 200
        do {
          const { data } = await axios.get(
            `/api/v2/project/${this.projectId}/warehouse/materials`,
            { params: { ...baseParams, page: currentPage } },
          )
          const pageItems = this.extractArrayFromResponse(data, 'materials')
          allMaterials.push(...pageItems)
          lastPage = Number(data && data.lastPage) || 1
          currentPage++
        } while (currentPage <= lastPage && currentPage <= MAX_PAGES)

        this.groupedTableTotal = allMaterials.length
        this.groupedCatalogRaw = allMaterials
      } catch (e) {
        this.groupedTableTotal = 0
        this.groupedCatalogRaw = []
      } finally {
        this.isGroupedCatalogLoading = false
      }
    },
    formatQuantity(value) {
      if (value == null || Number.isNaN(Number(value))) return '-'
      return Number(value).toLocaleString('es-ES', { maximumFractionDigits: 2 })
    },
    groupedTableRowClass(row) {
      if (row && row.is_pending_assignment) {
        return 'warehouse-group-pending-material'
      }
      return ''
    },
    async onExportInventorySheet() {
      this.isExporting = true
      try {
        const res = await axios.post(
          '/api/v2/export/warehouse-inventory-sheet',
          { filters: { project_id: this.projectId } },
          { responseType: 'blob' }
        )
        const contentDisposition = (res && res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || ''
        const match = /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i.exec(contentDisposition)
        const filename = (match && match[1] ? decodeURIComponent(match[1].replace(/"/g, '').trim()) : '') || 'Informe_STO_Stock.xlsx'

        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        // keep UI stable even if backend replies with non-blob error
        try {
          const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || this.$t('error')
          if (this.$buefy && this.$buefy.toast && this.$buefy.toast.open) {
            this.$buefy.toast.open({ message: msg, type: 'is-danger' })
          }
        } catch (_) {
          // ignore toast rendering errors
        }
        // eslint-disable-next-line no-console
        console.error(e)
      } finally {
        this.isExporting = false
      }
    },
    async openAddMaterialModal() {
      if (this.$refs.addMaterialModal && this.$refs.addMaterialModal.open) {
        await this.$refs.addMaterialModal.open()
      }
    },
    openEditGroupedMaterial(row) {
      if (!row || row.is_pending_assignment || row.warehouse_material_id == null) return

      const materialId = Number(row.warehouse_material_id)
      if (!Number.isFinite(materialId) || materialId < 1) return

      const material = (this.groupedCatalogRaw || []).find((m) => Number(m.id) === materialId)
      if (!material || !this.$refs.addMaterialModal || !this.$refs.addMaterialModal.openEdit) return

      this.$refs.addMaterialModal.openEdit({
        id: material.id,
        code: material.code,
        description: material.description,
        unit_id: material.unit_id,
        category: material.category,
        order_number: material.order_number,
        warehouse_provider_id: material.warehouse_provider_id,
        drawing_number: material.drawing_number,
        tag_kks: material.tag_kks,
        activity_id: material.activity_id,
        activity: material.activity,
      })
    },
    formatMaterialOption(m) {
      if (!m) return ''
      return `${m.code || '-'} - ${m.description || '-'}`
    },
    formatProviderOption(p) {
      if (!p) return ''
      return (p.code ? `${p.code} - ` : '') + (p.name || '-')
    },
    firstValidationMessage(errorData) {
      const err = errorData && errorData.errors
      if (err && typeof err === 'object') {
        const firstKey = Object.keys(err)[0]
        if (firstKey && Array.isArray(err[firstKey]) && err[firstKey].length) {
          return err[firstKey][0]
        }
      }
      if (errorData && errorData.message) {
        return errorData.message
      }
      return null
    },
    onDeleteGroupedMaterial(row) {
      if (!row || row.is_pending_assignment || row.warehouse_material_id == null) return
      if (Number(row.elements_associated) > 0) return

      const materialId = Number(row.warehouse_material_id)
      if (!Number.isFinite(materialId) || materialId < 1) return

      const label = row.unique_material_id && row.unique_material_id !== '-'
        ? row.unique_material_id
        : (row.description || String(materialId))

      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_generic', [label]),
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          this.deletingMaterialId = materialId
          try {
            await axios.delete(`/api/v2/project/${this.projectId}/warehouse/materials/${materialId}`)
            this.$buefy.toast.open({
              message: this.$t('deleted_successfully'),
              type: 'is-success',
              position: 'is-top-right',
            })
            await this.loadCatalogs()
            await this.loadMaterialsGroupedCatalog()
          } catch (e) {
            const resp = e.response && e.response.data
            const msg = this.firstValidationMessage(resp) || this.$t('error')
            this.$buefy.toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
            console.error(e)
          } finally {
            this.deletingMaterialId = null
          }
        },
      })
    },
    async onWarehouseMaterialSaved() {
      await this.loadCatalogs()
      if (this.activeSubTab === 1) {
        await this.loadMaterialsGroupedCatalog()
      }
    },
    async onInventoryDeleted() {
      if (this.activeSubTab === 1) {
        await this.loadMaterialsGroupedCatalog()
      }
    },
  },
}
</script>

<style scoped>
::v-deep .warehouse-grouped-table .table th,
::v-deep .warehouse-grouped-table .table td {
  font-size: 0.8125rem;
  padding: 0.45em 0.55em;
  vertical-align: middle;
}
::v-deep .warehouse-grouped-table .table th {
  font-size: 0.75rem;
  font-weight: 600;
}
::v-deep tr.warehouse-group-pending-material > td {
  background-color: #fffbeb;
  border-top: 2px solid #f59e0b;
}
::v-deep tr.warehouse-group-pending-material:hover > td {
  background-color: #fef3c7;
}
.warehouse-pending-unique-label {
  font-weight: 600;
  color: #b45309;
}
.warehouse-pending-description {
  font-size: 0.8125rem;
  color: #92400e;
}
.warehouse-detail-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}
::v-deep td.warehouse-stock-text-cell {
  min-width: 120px;
  max-width: 280px;
}
::v-deep .warehouse-stock-text-cell__content {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
}
::v-deep tr.detail > td {
  max-width: 0;
}
.stock-warehouse-view-toggle ::v-deep .tabs {
  margin-bottom: 0 !important;
}
.stock-warehouse-view-toggle ::v-deep .tab-content {
  display: none;
}
</style>
